import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import knowledgeApi from '@/lib/knowledge-api'

export interface Integration {
  id: number
  chat_assistant_id: number
  integration_type: 'openai_compatible' | 'generic_api' | 'crawler' | 'sro'
  name: string
  endpoint_url: string
  api_key_masked: string
  auth_header_type: string
  custom_auth_header_name: string
  is_openai_compatible: boolean
  response_json_path: string
  description: string
  routing_examples: string
  routing_examples_list: string[]
  requires_session: boolean
  session_key_name: string
  session_end_strategy: string
  session_timeout_seconds: number
  session_end_keywords: string
  session_end_response_field: string
  custom_headers: string
  custom_headers_dict: Record<string, string>
  crawler_include_domains: string
  crawler_exclude_domains: string
  crawler_max_results: number
  crawler_search_depth: 'basic' | 'advanced'
  crawler_topic: 'general' | 'news' | 'finance'
  crawler_time_range: '' | 'day' | 'week' | 'month' | 'year'
  crawler_include_answer: 'off' | 'basic' | 'advanced'
  crawler_include_raw_content: boolean
  crawler_include_images: boolean
  crawler_include_image_descriptions: boolean
  crawler_country: string
  crawler_query_prefix: string
  sro_config_id: number | null
  sro_config_name: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface CreateIntegrationData {
  integration_type: 'openai_compatible' | 'generic_api' | 'crawler' | 'sro'
  name: string
  endpoint_url?: string
  api_key?: string
  auth_header_type?: string
  custom_auth_header_name?: string
  is_openai_compatible?: boolean
  response_json_path?: string
  description?: string
  routing_examples: string
  requires_session?: boolean
  session_key_name?: string
  session_end_strategy?: string
  session_timeout_seconds?: number
  session_end_keywords?: string
  session_end_response_field?: string
  custom_headers?: string
  crawler_include_domains?: string
  crawler_exclude_domains?: string
  crawler_max_results?: number
  crawler_search_depth?: 'basic' | 'advanced'
  crawler_topic?: 'general' | 'news' | 'finance'
  crawler_time_range?: '' | 'day' | 'week' | 'month' | 'year'
  crawler_include_answer?: 'off' | 'basic' | 'advanced'
  crawler_include_raw_content?: boolean
  crawler_include_images?: boolean
  crawler_include_image_descriptions?: boolean
  crawler_country?: string
  crawler_query_prefix?: string
  sro_config_id?: number
  is_active?: boolean
}

export interface UpdateIntegrationData {
  name?: string
  endpoint_url?: string
  api_key?: string
  auth_header_type?: string
  custom_auth_header_name?: string
  is_openai_compatible?: boolean
  response_json_path?: string
  description?: string
  routing_examples?: string
  requires_session?: boolean
  session_key_name?: string
  session_end_strategy?: string
  session_timeout_seconds?: number
  session_end_keywords?: string
  session_end_response_field?: string
  custom_headers?: string
  crawler_include_domains?: string
  crawler_exclude_domains?: string
  crawler_max_results?: number
  crawler_search_depth?: 'basic' | 'advanced'
  crawler_topic?: 'general' | 'news' | 'finance'
  crawler_time_range?: '' | 'day' | 'week' | 'month' | 'year'
  crawler_include_answer?: 'off' | 'basic' | 'advanced'
  crawler_include_raw_content?: boolean
  crawler_include_images?: boolean
  crawler_include_image_descriptions?: boolean
  crawler_country?: string
  crawler_query_prefix?: string
  sro_config_id?: number
  is_active?: boolean
}

export interface TestConnectionResult {
  status: 'success' | 'error'
  message: string
}

// List integrations for an assistant
export function useIntegrations(assistantId: string | undefined, activeOnly?: boolean) {
  return useQuery({
    queryKey: ['integrations', assistantId, activeOnly],
    queryFn: async () => {
      const params = activeOnly ? '?active_only=true' : ''
      const response = await knowledgeApi.get(`/knowledge/api/assistants/${assistantId}/integrations/${params}`)
      return response.data.integrations as Integration[]
    },
    enabled: !!assistantId,
  })
}

// Get single integration
export function useIntegration(integrationId: number | undefined) {
  return useQuery({
    queryKey: ['integration', integrationId],
    queryFn: async () => {
      const response = await knowledgeApi.get(`/knowledge/api/integrations/${integrationId}/`)
      return response.data as Integration
    },
    enabled: !!integrationId,
  })
}

// Create integration
export function useCreateIntegration() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ assistantId, data }: { assistantId: string; data: CreateIntegrationData }) => {
      const response = await knowledgeApi.post(`/knowledge/api/assistants/${assistantId}/integrations/create/`, data)
      return response.data
    },
    onSuccess: (_, { assistantId }) => {
      queryClient.invalidateQueries({ queryKey: ['integrations', assistantId] })
    },
  })
}

// Update integration
export function useUpdateIntegration() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ integrationId, data }: { integrationId: number; data: UpdateIntegrationData }) => {
      const response = await knowledgeApi.put(`/knowledge/api/integrations/${integrationId}/update/`, data)
      return response.data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] })
      queryClient.invalidateQueries({ queryKey: ['integration', data.integration?.id] })
    },
  })
}

// Delete integration
export function useDeleteIntegration() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (integrationId: number) => {
      const response = await knowledgeApi.delete(`/knowledge/api/integrations/${integrationId}/delete/`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] })
    },
  })
}

// Toggle integration active status
export function useToggleIntegration() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ integrationId, isActive }: { integrationId: number; isActive: boolean }) => {
      const response = await knowledgeApi.patch(`/knowledge/api/integrations/${integrationId}/toggle/`, {
        is_active: isActive,
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] })
    },
  })
}

// Test integration connection
export function useTestIntegration() {
  return useMutation({
    mutationFn: async (integrationId: number) => {
      const response = await knowledgeApi.post(`/knowledge/api/integrations/${integrationId}/test/`)
      return response.data as TestConnectionResult
    },
  })
}

// Intent conflict type
export interface IntentConflict {
  example1: string
  route1_id: string
  route1_name: string
  example2: string
  route2_id: string
  route2_name: string
  reason: string
}

// Route for validation
export interface ValidationRoute {
  id: string
  name: string
  examples: string[]
}

// Validate intent examples for overlaps
export function useValidateIntents() {
  return useMutation({
    mutationFn: async ({ assistantId, routes }: { assistantId: string; routes: ValidationRoute[] }) => {
      const response = await knowledgeApi.post(`/knowledge/api/assistants/${assistantId}/validate-intents/`, {
        routes,
      })
      return response.data as { conflicts: IntentConflict[]; message: string }
    },
  })
}
